/** Acquisition stages for the Deal CRM.
 *
 * Order and slugs must match STAGES in app/services/deal_pipeline.py and the
 * CHECK constraint in db/migrations/2026_09_07_remove_loi_accepted_stage.sql.
 *
 * Accent tints are translucent 500s so the column headers and card dots read on
 * both the light and dark card surfaces. LOI submitted → diligence walk violet
 * → indigo, so the two related steps read as a run.
 */
export interface DealStage {
  slug: string;
  label: string;
  accent: string;
  dot: string;
  chip: string;
}

export const STAGES: DealStage[] = [
  { slug: "watching",      label: "Watching",      accent: "text-sky-500",     dot: "bg-sky-500",     chip: "bg-sky-500/15" },
  { slug: "contacted",     label: "Contacted",     accent: "text-amber-500",   dot: "bg-amber-500",   chip: "bg-amber-500/15" },
  { slug: "loi_submitted", label: "LOI Submitted", accent: "text-violet-500",  dot: "bg-violet-500",  chip: "bg-violet-500/15" },
  { slug: "due_diligence", label: "Due Diligence", accent: "text-indigo-400",  dot: "bg-indigo-400",  chip: "bg-indigo-400/15" },
  { slug: "financing",     label: "Financing",     accent: "text-emerald-500", dot: "bg-emerald-500", chip: "bg-emerald-500/15" },
  { slug: "closing",       label: "Closing",       accent: "text-primary",     dot: "bg-primary",     chip: "bg-primary/15" },
];

/** Stages that count as late-stage momentum on the KPI tile. */
export const LATE_STAGES = new Set(["due_diligence", "financing", "closing"]);

/** Stages that no longer exist on the board but still appear in the stored
 *  note history ("moved LOI Submitted → LOI Accepted"). The history is a record
 *  of what was actually done, so it is never rewritten — it just needs a label
 *  that reads properly after the stage was retired. */
const RETIRED_STAGE_LABELS: Record<string, string> = {
  loi_accepted: "LOI Accepted",
};

export const stageBySlug = (slug: string): DealStage | undefined =>
  STAGES.find((s) => s.slug === slug);

export function stageLabel(slug: string | null | undefined): string {
  if (!slug) return "—";
  return stageBySlug(slug)?.label ?? RETIRED_STAGE_LABELS[slug] ?? slug.replace(/_/g, " ");
}
